import {
  Fragment,
  type Key,
  type PropsWithChildren,
  type ReactElement,
  cloneElement,
  useMemo,
} from 'react';

import { hasValue } from '@core/utils/equality';

interface Section<T> {
  name: string;
  children: T[];
}

interface SectionListProps<T> {
  sectionWrapper?: ReactElement;
  sections: Section<T>[];
  keyExtractor(props: T, idx: number): string;
  sectionKeyExtractor(props: Section<T>, idx: number): string;
  renderSectionHeader?(props: { section: Section<T>; index: number }): ReactElement;
  renderItem(props: { item: T; index: number }): ReactElement;
}

export interface ElementWithKey extends PropsWithChildren {
  key: Key;
}

export const SectionList = <T,>({
  sections,
  renderItem,
  keyExtractor,
  sectionKeyExtractor,
  renderSectionHeader,
  sectionWrapper,
}: SectionListProps<T>): ReactElement => {
  const SectionWrapper = useMemo(() => {
    if (hasValue(sectionWrapper))
      return (props: ElementWithKey): ReactElement => cloneElement(sectionWrapper, props);

    return Fragment;
  }, [sectionWrapper]);

  return (
    <>
      {sections.map((section, sectionIdx) => (
        <SectionWrapper key={sectionKeyExtractor(section, sectionIdx)}>
          {renderSectionHeader?.({ section, index: sectionIdx })}

          {section.children.map((item, index) => (
            <Fragment key={keyExtractor(item, index)}>{renderItem({ item, index })}</Fragment>
          ))}
        </SectionWrapper>
      ))}
    </>
  );
};
